import React, { useState, useEffect } from "react";
import {
  createTheme,
  ThemeProvider,
  useColorScheme,
  useMediaQuery,
} from "@mui/material";
import InterTTF from "../../assets/fonts/Inter/Inter-VariableFont_opsz,wght.ttf";

export const Colors = {
  gray: {
    0: "#FFFFFF",
    50: "#FAFAFA",
    100: "#F4F4F5",
    200: "#E4E4E7",
    300: "#D4D4D8",
    400: "#A1A1AA",
    500: "#71717A",
    600: "#52525B",
    700: "#3F3F46",
    800: "#27272A", //standard text color
    900: "#18181B",
  },
  cyan: {
    200: "#66E0F2",
    300: "#33DDEE",
    500: "#00CCDD",
    600: "#00B3C4",
    700: "#0099AA",
    800: "#176574",
  },
  darkBlue: {
    300: "#364153",
    600: "#1E2939",
    800: "#101828",
    900: "#030712",
  },
  red: {
    300: "#FFA2A2",
    400: "#FF6467",
    500: "#FB2C36",
  },
  green: {
    50: "#F0FDF4",
    100: "#DCFCE7",
    300: "#05DF72",
    500: "#00A63E",
    700: "#008236",
  },
  hotPink: {
    500: "#F6339A",
  },
  purple: {
    200: "#E9D4FF",
    400: "#C27AFF",
    500: "#AD46FF",
  },
  yellow: {
    100: "#FEF9C2",
    500: "#F0B100",
    700: "#A65F00",
  },
  gold: {
    200: "#FEE685",
    300: "#FDC700",
    500: "#D08700",
  },
  orange: {
    50: "#FFF3E0",
    500: "#FF9800",
  },
};

export const Typography = {
  fontFamily: "Inter, sans-serif",
  h1: {
    fontSize: "48px",
    fontWeight: 800,
    lineHeight: 1.2,
    color: Colors.gray[700],
  },
  h2: {
    fontSize: "36px",
    fontWeight: 700,
    lineHeight: 1.1,
    color: Colors.gray[700],
  },
  h3: {
    fontSize: "32px",
    fontWeight: 700,
    lineHeight: 1.3,
    color: Colors.gray[700],
  },
  h4: {
    fontSize: "20px",
    fontWeight: 700,
    lineHeight: 1.4,
    color: Colors.gray[700],
  },
  h5: {
    fontSize: "18px",
    fontWeight: 700,
    lineHeight: 1.5,
    color: Colors.gray[700],
  },
  subtitle1: {
    fontSize: "16px",
    fontWeight: 700,
    lineHeight: 1.5,
    color: Colors.gray[700],
  },
  subtitle2: {
    fontSize: "14px",
    fontWeight: 700,
    lineHeight: 1.4,
    color: Colors.gray[700],
  },
  body1: {
    fontSize: "16px",
    fontWeight: 400,
    lineHeight: 1.6,
    color: Colors.gray[700],
  },
  body2: {
    fontSize: "14px",
    fontWeight: 400,
    lineHeight: 1.6,
    color: Colors.gray[700],
  },
  placeholder: {
    fontSize: "16px",
    fontWeight: 400,
    lineHeight: 1.25,
    color: Colors.gray[400],
  },
  button: {
    fontSize: "16px",
    fontWeight: 700,
    lineHeight: 1,
    textTransform: "none",
    color: Colors.gray[700],
  },
  caption: {
    fontSize: "12px",
    fontWeight: 400,
    lineHeight: 1.3,
    color: Colors.gray[600],
  },
  overline: {
    letterSpacing: 2,
  },
};

export const Overrides = {
  // Style sheet name ⚛️
  MuiCardActionArea: {
    root: {
      "&:hover $focusHighlight": {
        opacity: "0.1",
      },
      "&:active $focusHighlight": {
        opacity: "0.1",
      },
    },
    focusHighlight: {
      backgroundColor: Colors.gray[500],
    },
  },
  MuiCard: {
    root: {
      borderRadius: 0,
    },
  },
  MuiButton: {
    containedPrimary: {
      color: Colors.gray[0],
    },
    root: {
      textTransform: "capitalize",
      borderRadius: "10px",
    },
  },
  MuiTab: {
    wrapper: {
      minWidth: 90,
    },
    root: {
      textTransform: "none",
      letterSpacing: 0,
      color: Colors.gray[500],
      fontWeight: 400,
      minWidth: "90px !important",
    },
  },
  MuiTabs: {
    root: {
      color: Colors.gray[500],
    },
    indicator: {
      color: Colors.gray[900],
    },
  },
};

const appTheme = (mode) =>
  createTheme({
    breakpoints: {
      keys: ["xs", "sm", "md", "lg", "xl"],
      values: {
        xs: 0,
        sm: 700,
        md: 1020,
        lg: 1232,
        xl: 1288,
      },
    },
    branding: {
      teal: Colors.cyan[500],
      softTeal: Colors.cyan[300],
      // Standardized color approach
      ...Colors,
    },
    palette: {
      primary: {
        light: Colors.cyan[200],
        main: Colors.cyan[500],
        dark: Colors.cyan[700],
        contrastText: Colors.gray[900],
      },
      secondary: {
        light: Colors.cyan[200],
        main: Colors.cyan[500],
        dark: Colors.cyan[700],
        contrastText: Colors.gray[0],
      },
    },
    colorSchemes: {
      dark: {
        text: {
          primary: Colors.gray[0],
          secondary: Colors.gray[400],
          alpha: "rgba(231, 231, 231, .44)", //#e7e7e770
        },
        background: {
          paper: Colors.darkBlue[800],
          default: Colors.darkBlue[900],
        },
        backgroundAlpha: Colors.darkBlue[800],
        typography: {
          ...Typography,
          h1: {
            ...Typography.h1,
            color: Colors.gray[0],
          },
          h2: {
            ...Typography.h2,
            color: Colors.gray[0],
          },
          h3: {
            ...Typography.h3,
            color: Colors.gray[0],
          },
          h4: {
            ...Typography.h4,
            color: Colors.gray[0],
          },
          h5: {
            ...Typography.h4,
            color: Colors.gray[0],
          },
          subtitle1: {
            ...Typography.subtitle1,
            color: Colors.gray[0],
          },
          subtitle2: {
            ...Typography.subtitle2,
            color: Colors.gray[0],
          },
          body1: {
            ...Typography.body,
            color: Colors.gray[0],
          },
          body2: {
            ...Typography.body2,
            color: Colors.gray[0],
          },
          caption: {
            ...Typography.caption,
            color: Colors.gray[400],
          },
        },
        components: {
          MuiPaper: {
            root: {
              backgroundColor: Colors.darkBlue[900],
              background: Colors.darkBlue[900],
            },
          },
        },
        overrides: {
          ...Overrides,
          MuiPaper: {
            root: {
              backgroundColor: Colors.darkBlue[900],
              background: Colors.darkBlue[900],
            },
            defaultProps: {
              sx: {
                backgroundColor: Colors.darkBlue[900],
                background: Colors.darkBlue[900],
              },
            },
          },
          MuiOutlinedInput: {
            input: {
              "&:-webkit-autofill": {
                WebkitBoxShadow: `0 0 0 100px ${Colors.gray[700]} inset`,
                WebkitTextFillColor: Colors.gray[0],
              },
            },
          },
          MuiIconButton: {
            root: {
              color: Colors.gray[0],
            },
          },
          MuiTab: {
            ...Overrides.MuiTab,
            root: {
              ...Overrides.MuiTab.root,
              "&$selected": {
                color: Colors.gray[0],
                fontWeight: 500,
              },
            },
            textColorInherit: {
              color: Colors.gray[400],
              opacity: 1,
            },
          },
        },
      },
      light: {
        text: {
          primary: Colors.gray[900],
          secondary: Colors.gray[900],
          alpha: "rgba(24, 24, 24, .44)", //#18181870
        },
        background: {
          paper: Colors.gray[100],
          default: Colors.gray[0],
        },
        backgroundAlpha: "rgba(255, 255, 255, .95)", //#fffffff2
        typography: {
          ...Typography,
        },
        overrides: {
          ...Overrides,
          MuiOutlinedInput: {
            input: {
              "&:-webkit-autofill": {
                WebkitBoxShadow: `0 0 0 100px ${Colors.gray[100]} inset`,
                WebkitTextFillColor: Colors.gray[900],
              },
            },
          },
          MuiIconButton: {
            root: {
              color: Colors.gray[900],
            },
          },
          MuiTab: {
            ...Overrides.MuiTab,
            root: {
              ...Overrides.MuiTab.root,
              "&$selected": {
                color: Colors.gray[900],
                fontWeight: 500,
              },
            },
            textColorInherit: {
              color: Colors.gray[500],
              opacity: 1,
            },
          },
        },
      },
    },
    spacing: 7,
    components: {
      MuiCssBaseline: {
        styleOverrides: `
        @font-face {
          font-family: 'Inter';
          font-style: normal;
          font-display: swap;
          font-weight: 400;
          src: local('Inter'), local('Inter'), url(${InterTTF}) format('truetype');
        }
      `,
      },
    },
    overrides: {
      ...Overrides,
    },
  });

const Theme = (props) => {
  const { mode, setMode } = useColorScheme();

  const prefersDarkMode = useMediaQuery("(prefers-color-scheme: dark)");

  useEffect(() => {
    if (!localStorage.getItem("darkMode") && !mode) {
      setMode(prefersDarkMode ? "dark" : "light");
    }
  }, [prefersDarkMode, mode, setMode]);

  return (
    <ThemeProvider theme={appTheme(mode)} defaultMode="light" noSsr>
      {props.children}
    </ThemeProvider>
  );
};

export default Theme;
